import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { 
  PlusIcon, 
  CalendarDaysIcon, 
  PhoneIcon,
  ClockIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';

const STATUS_PRIORITY = {
  'pending': 1,
  'confirmed': 2,
  'cancelled': 3,
};

const BookingList = ({ 
  bookings = [], 
  services = [], 
  onBookingCreate,
  onBookingDelete, 
}) => {
  const {t} = useTranslation();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [filterType, setFilterType] = useState('today'); // 'today' | 'week' | 'month' | 'all'
  
  const [formData, setFormData] = useState({
    service_id: '',
    client_name: '',
    phone: '',
    pet_name: '',
    booking_date: moment().format('YYYY-MM-DD'),
    date_time: '10:00',
    notes: ''
  });

  // --- ЛОГИКА ФИЛЬТРАЦИИ И СОРТИРОВКИ ---
  const processedBookings = bookings
    .filter(booking => {
      const bDate = moment(booking.date_time || booking.booking_date).local();
      const now = moment();

      switch (filterType) {
        case 'today': return bDate.isSame(now, 'day');
        case 'week': return bDate.isSame(now, 'week');
        case 'month': return bDate.isSame(now, 'month');
        default: return true;
      }
    })
    .sort((a, b) => {
      // 1. Сортировка по весу статуса (pending выше всех)
      const priorityA = STATUS_PRIORITY[a.status] || 99;
      const priorityB = STATUS_PRIORITY[b.status] || 99;
      
      if (priorityA !== priorityB) {
        return priorityA - priorityB;
      }
      
      // 2. Внутри одного статуса — по времени (ранние выше)
      return moment(a.date_time || a.booking_date).local().valueOf() - 
       moment(b.date_time || b.booking_date).local().valueOf();
    });


("BOOKINGS:", processedBookings[0]);

const handleSubmit = async (e) => {
  e.preventDefault();

  const fullDateTime = moment(
    `${formData.booking_date} ${formData.date_time}`,
    'YYYY-MM-DD HH:mm'
  ).utc().format();

  const payload = {
    service_id: String(formData.service_id),
    client_name: formData.client_name,
    phone: formData.phone,
    pet_name: formData.pet_name.trim() || "",
    date_time: fullDateTime,
    notes: formData.notes || "",
    booking_date: fullDateTime,
    duration: 60
  };
try{
  if (editingBooking) {
    // ✅ РЕДАКТИРОВАНИЕ
    await onBookingEdit(editingBooking.id,payload);
  } else {
    // ✅ СОЗДАНИЕ
    await onBookingCreate(payload);
  }

  // ✅ СБРОС СОСТОЯНИЯ
  setEditingBooking(null);
  setShowCreateModal(false);
}
catch{console.error("Update error",err)}
  setFormData({
    service_id: '',
    client_name: '',
    phone: '',
    pet_name: '',
    booking_date: moment().format('YYYY-MM-DD'),
    date_time: '10:00',
    notes: ''
  });
  ("editingBooking:", editingBooking);
};


  const [editingBooking, setEditingBooking] = useState(null);



  return (
    <div className="max-w-4xl mx-auto p-6 space-y-8">
      {/* Header */}
      <div className="flex justify-between items-end">
        <div>
          <p className="text-zinc-500 font-medium italic">{t("company.calendar.booking_list.header.title")}</p>
        </div>
        <button 
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 bg-orange-500 text-white px-6 py-3 rounded-2xl font-bold hover:bg-orange-600 transition-all shadow-lg shadow-orange-100"
        >
          <PlusIcon className="w-5 h-5" />
          {t("company.calendar.booking_list.header.btn_new")}
        </button>
      </div>

      {/* Переключатель фильтров */}
      <div className="flex bg-zinc-100 p-1.5 rounded-2xl w-fit">
        {[
          { id: 'today', label: t("company.calendar.booking_list.filters.today") },
          { id: 'week', label: t("company.calendar.booking_list.filters.week") },
          { id: 'month', label: t("company.calendar.booking_list.filters.month") },
          { id: 'all', label: t("company.calendar.booking_list.filters.all") },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setFilterType(tab.id)}
            className={`px-6 py-2 rounded-xl text-xs font-black uppercase tracking-wider transition-all ${
              filterType === tab.id 
                ? 'bg-white text-zinc-900 shadow-sm' 
                : 'text-zinc-400 hover:text-zinc-600'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Список записей */}
      <div className="grid gap-4">
        {processedBookings.length === 0 ? (
          <div className="p-16 text-center bg-zinc-50 rounded-[40px] border-2 border-dashed border-zinc-200">
            <CalendarDaysIcon className="w-12 h-12 text-zinc-300 mx-auto mb-4" />
            <p className="text-zinc-500 font-bold">{t("company.calendar.booking_list.list.empty")}</p>
          </div>
        ) : (
          processedBookings.map((booking) => (
            <div 
              key={booking.id}
              className={`bg-white p-5 rounded-[28px] border transition-all flex items-center justify-between group ${
                booking.status === 'pending' 
                ? 'border-orange-200 shadow-md shadow-orange-50' 
                : 'border-zinc-100 shadow-sm'
              }`}
            >
              <div className="flex items-center gap-5">
                <div className={`p-3 rounded-2xl text-center min-w-[80px] ${
                  booking.status === 'pending' ? 'bg-orange-50' : 'bg-zinc-50'
                }`}>
                  <p className="text-[10px] font-black uppercase text-zinc-400">{t("company.calendar.booking_list.list.time_label")}</p>
                  <p className={`text-sm font-bold ${
                    booking.status === 'pending' ? 'text-orange-600' : 'text-zinc-900'
                  }`}>
                    {moment(booking.date_time || booking.booking_date).format('HH:mm')}
                  </p>
                </div>
                
                <div>
                  <h4 className="font-bold text-zinc-900 text-lg flex items-center gap-2">
                    {booking.client_name}
                    {booking.pet?.pet_name && (
                      <span className="text-[10px] bg-orange-50 text-orange-600 px-2 py-0.5 rounded-lg font-black uppercase">
                        {t("company.calendar.booking_list.list.pet_prefix")} {booking.pet?.pet_name}
                      </span>
                    )}
                  </h4>
                  <div className="flex gap-4 mt-1 text-zinc-500 text-xs font-medium">
                    <span className="flex items-center gap-1">
                      <ClockIcon className="w-3.5 h-3.5" /> {booking.service_name || t("company.calendar.booking_list.list.service_default")}
                    </span>
                    <span className="flex items-center gap-1">
                      <PhoneIcon className="w-3.5 h-3.5" /> {booking.customer?.phone}
                    </span>
                    {/* Показываем дату, если смотрим неделю или месяц */}
                    {filterType !== 'today' && (
                       <span className="text-orange-400 font-bold uppercase tracking-tighter">
                        • {moment(booking.date_time || booking.booking_date).format('DD MMM')}
                       </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider ${getStatusStyles(booking.status)}`}>
                  {t(`company.calendar.booking_list.statuses.${booking.status}`)}
                </span>
                {/* 
                Delete button
                <button
                  onClick={async () => {
                    const confirmDelete = window.confirm("Удалить запись?");
                    if (!confirmDelete) return;

                    try {
                      await onBookingDelete(booking.id);
                    } catch (err) {
                      console.error("Delete error:", err);
                    }
                  }}
                  className="p-2 rounded-xl bg-red-100 hover:bg-red-200 transition-all"
                >
                  <XCircleIcon className="w-5 h-5 text-red-500 hover:text-red-700" />
                </button> */}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Модалка создания */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-zinc-900/60 backdrop-blur-sm">
          <div className="bg-white w-full max-w-lg rounded-[36px] p-8 shadow-2xl overflow-hidden relative">
            <div className="flex justify-between items-center mb-8">
              <h3 className="text-2xl font-black text-zinc-900">{t("company.calendar.booking_list.modal.title_create")}</h3>
              <button onClick={() => {
              setShowCreateModal(false);
              setEditingBooking(null);
            }} className="text-zinc-400 hover:text-zinc-900 transition-colors">
                <XCircleIcon className="w-8 h-8" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-[10px] font-black uppercase text-zinc-400 ml-1">{t("company.calendar.booking_list.modal.labels.date")}</label>
                  <input 
                    type="date" 
                    required
                    className="w-full bg-zinc-50 border-none rounded-2xl p-4 font-bold focus:ring-2 focus:ring-orange-500 transition-all"
                    value={formData.booking_date}
                    onChange={e => setFormData({...formData, booking_date: e.target.value})}
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black uppercase text-zinc-400 ml-1">{t("company.calendar.booking_list.modal.labels.time")}</label>
                  <input 
                    type="time" 
                    required
                    className="w-full bg-zinc-50 border-none rounded-2xl p-4 font-bold focus:ring-2 focus:ring-orange-500 transition-all"
                    value={formData.date_time}
                    onChange={e => setFormData({...formData, date_time: e.target.value})}
                  />
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-[10px] font-black uppercase text-zinc-400 ml-1">{t("company.calendar.booking_list.modal.labels.service")}</label>
                <select 
                  required
                  className="w-full bg-zinc-50 border-none rounded-2xl p-4 font-bold focus:ring-2 focus:ring-orange-500 transition-all appearance-none"
                  value={formData.service_id}
                  onChange={e => setFormData({...formData, service_id: e.target.value})}
                >
                  <option value="">{t("company.calendar.booking_list.modal.placeholders.service_option")}</option>
                  {services.map(s => <option key={s.id} value={s.id}>{s.name} — {s.price} TMT</option>)}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <input 
                  placeholder= {t("company.calendar.booking_list.modal.labels.client")}
                  required
                  className="w-full bg-zinc-50 border-none rounded-2xl p-4 font-bold focus:ring-2 focus:ring-orange-500 transition-all"
                  value={formData.client_name}
                  onChange={e => setFormData({...formData, client_name: e.target.value})}
                />
                <input 
                  placeholder={t("company.calendar.booking_list.modal.labels.phone")}
                  required
                  type="tel"
                  className="w-full bg-zinc-50 border-none rounded-2xl p-4 font-bold focus:ring-2 focus:ring-orange-500 transition-all"
                  value={formData.phone}
                  onChange={e => setFormData({...formData, phone: e.target.value})}
                />
              </div>

              <input 
                placeholder={t("company.calendar.booking_list.modal.labels.pet")}
                className="w-full bg-zinc-50 border-none rounded-2xl p-4 font-bold focus:ring-2 focus:ring-orange-500 transition-all"
                value={formData.pet_name}
                onChange={e => setFormData({...formData, pet_name: e.target.value})}
              />

              <div className="space-y-1">
                <label className="text-[10px] font-black uppercase text-zinc-400 ml-1">{t("company.calendar.booking_list.modal.labels.notes")}</label>
                <textarea 
                  placeholder={t("company.calendar.booking_list.modal.placeholders.notes")}
                  className="w-full bg-zinc-50 border-none rounded-2xl p-4 font-bold focus:ring-2 focus:ring-orange-500 transition-all min-h-[80px]"
                  value={formData.notes}
                  onChange={e => setFormData({...formData, notes: e.target.value})}
                />
              </div>

              <button 
                type="submit"
                className="w-full py-5 bg-zinc-900 text-white rounded-2xl font-black text-lg shadow-xl shadow-zinc-200 hover:bg-black transition-all mt-4"
              >
                {t("company.calendar.booking_list.modal.submit")}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const getStatusStyles = (status) => {
  switch (status) {
    case 'confirmed': return 'bg-green-50 text-green-600';
    case 'pending': return 'bg-orange-100 text-orange-600 animate-pulse'; 
    case 'cancelled': return 'bg-rose-50 text-rose-600';
    default: return 'bg-zinc-100 text-zinc-500';
  }
};

export default BookingList;